import { escapeHtml, toast, detailsHash } from "./util.js";
import { cachedGroupDetail, fetchGroupDetail } from "./meta-cache.js";
import { formatShortWeekdayDateTime, isUnscheduled } from "./date-utils.js";
import { renderComposeArea } from "./compose.js";
import { t } from "./i18n.js";
import { mediaCache } from "./media-cache.js";
import { getPreview, putPreview } from "./media-store.js";
import {
  DOWNLOAD_TIMEOUT_MESSAGE, DOWNLOAD_TIMEOUT_MS, downloadWithTimeout,
  mediaDownloadLimit, photoPreviewSize, resetMediaSenders,
} from "./download.js";
import type { GroupDetail } from "./api-types.js";

// Stable colour per author — hashes to a hue on the HSL wheel, then spaces
// authors evenly for maximum contrast. Saturation 70%, lightness 40%.
const AUTHOR_SAT = 70;
const AUTHOR_LIT = 40;
function authorColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(h) % 360},${AUTHOR_SAT}%,${AUTHOR_LIT}%)`;
}
function authorBg(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return `hsla(${Math.abs(h) % 360},${AUTHOR_SAT}%,${AUTHOR_LIT}%,0.15)`;
}
function authorInitial(name: string): string {
  return name.charAt(0).toUpperCase();
}


/** No-op — groups are loaded into the sidebar by app.ts. */
export async function loadGroupsView(_state: any): Promise<void> {}

/** Load feed + compose into #main. Called from app.ts routeMain. */
export async function loadFeedInto(state: any, groupId: string): Promise<void> {
  // Stale-while-revalidate on the group metadata, mirroring historyCache one
  // layer up: a revisited feed paints instantly from cache (synchronously, so
  // routeMain's "Loading…" skeleton never reaches the screen) while the fresh
  // fetch proceeds in the background. First visit awaits the network as before.
  const freshPromise = fetchGroupDetail(groupId);
  const cached = cachedGroupDetail(groupId);
  let data: GroupDetail;
  if (cached) {
    data = cached;
    const metaEl = document.getElementById("feed-meta");
    void freshPromise
      .then((fresh) => {
        // Repaint the meta strip only if this feed is still on screen —
        // routeMain replaces #main's children on every navigation, so a
        // detached element means the user has moved on.
        if (metaEl?.isConnected) renderFeedMeta(fresh);
      })
      .catch(() => { /* stale meta stands; compose surfaces real errors */ });
  } else {
    data = await freshPromise;
  }
  renderFeedMeta(data);

  if (data.telegram_chat_id == null) {
    setFeed(`<p class="feed-empty">${t("feed.no_chat")}</p>`);
  } else {
    try {
      await renderGroupHistory(state.client, data.telegram_chat_id, data.message_authors);
    } catch (e) {
      console.error("history fetch failed", e);
      setFeed(`<p class="feed-empty">${t("feed.load_failed")}</p>`);
    }
  }

  // Land at the START of the last writer's letter, not the absolute bottom, so
  // you begin reading the newest letter from its first line. The last
  // .letter-group is the most recent author's grouped block.
  requestAnimationFrame(scrollToLastLetter);

  // Compose area below the letters. Pass the in-flight fresh metadata so
  // compose gates turn state on live data (never the cache) without a
  // duplicate /groups/{id} round-trip.
  const anchor = document.getElementById("compose-anchor");
  if (anchor) await renderComposeArea(anchor, state, groupId, freshPromise);
}

/** Render the writer/schedule/rotation strip into #feed-meta. */
function renderFeedMeta(data: GroupDetail): void {
  const writer = data.current_turn_display_name
    ? `${escapeHtml(data.current_turn_display_name)} is writing`
    : "No active writer";

  // The writing order is only fixed once the group is active (finalize
  // shuffles it). Before that, show an unordered member list — no "→" sequence
  // that implies an order about to be randomized.
  const isActive = data.status === "active" || data.status === "grace";
  const labelled = [...data.members]
    .sort((a, b) => a.rotation_position - b.rotation_position)
    .map((m) => {
      const you = m.is_you ? " (you)" : "";
      const cur = m.id === data.current_turn_member_id ? " ✍️" : "";
      return escapeHtml(m.display_name) + you + cur;
    });
  const rotation = isActive
    ? labelled.join(" → ")
    : labelled.join(", ") + " · order set when the group is locked";

  // Next concrete publication slot, in the viewer's LOCAL time. The bare
  // publication_hour_utc + weekday have no date, so they can't be localized;
  // next_publication_slot is a real datetime that can.
  let schedule = "";
  const slot = data.next_publication_slot;
  if (slot && !isUnscheduled(slot)) {
    schedule = t("feed.next_letter", { when: formatShortWeekdayDateTime(new Date(slot)) });
  }

  const meta = document.getElementById("feed-meta");
  if (meta) {
    meta.innerHTML = `
      <h2 style="font-size:1.2rem;margin-bottom:.25rem">${escapeHtml(data.name)}</h2>
      <p>${writer}${escapeHtml(schedule)}</p>
      <p class="feed-rotation">${rotation}</p>`;
  }

  // Settings link sits after the letters and before the draft area — where the
  // reader already is once they've caught up. Rendered here rather than in the
  // page shell because it names the correspondence, which only this data has.
  const anchor = document.getElementById("feed-settings-anchor");
  if (anchor) {
    anchor.innerHTML = `
      <a href="${detailsHash(data.group_id)}" class="feed-settings-link">
        <span class="feed-settings-gear" aria-hidden="true">⚙</span>
        <span>${escapeHtml(t("app.details", { name: data.name }))}</span>
      </a>`;
  }
}

/** Load settings into #main. */
export async function loadSettingsInto(state: any, groupId: string): Promise<void> {
  const { loadSettingsView } = await import("./settings.js");
  return loadSettingsView(state, groupId);
}

async function resolveChat(client: any, chatId: number): Promise<any> {
  try {
    return await client.getInputEntity(chatId);
  } catch {
    await client.getDialogs({ limit: 100 });
    return await client.getInputEntity(chatId);
  }
}

type MediaKind = "photo" | "video" | "image-doc" | "document";

interface MediaInfo {
  raw: any;            // GramJS message media, passed to downloadMedia
  kind: MediaKind;
  name?: string;       // document filename
  domId: string;       // placeholder element id for async swap
  cacheKey: string;    // "<chatId>:<msgId>" — media-cache key (ids collide across chats)
  w?: number;          // video pixel dimensions, for the aspect-ratio box
  h?: number;
}

/** Author sentinel for snail's own group notices (cadence/schedule change,
 *  member left, reorder). Mirrors storage.SNAIL_AUTHOR. These render as quiet
 *  notices between letters, not as letters — Telegram members see them inline,
 *  so without this the web feed was the only place a settings change was
 *  invisible. */
export const SNAIL_AUTHOR = "__snail__";

interface FeedMessage {
  text: string;
  author: string;
  date: Date;
  media?: MediaInfo;
}

interface LetterGroup {
  author: string;
  messages: Array<{ text: string; date: Date; media?: MediaInfo }>;
}

let mediaSeq = 0;

/** Classify a GramJS message's media, or null if none/unsupported. */
export function classifyMedia(
  media: any,
): { kind: MediaKind; name?: string; w?: number; h?: number } | null {
  if (!media) return null;
  if (media.className === "MessageMediaPhoto") return { kind: "photo" };
  if (media.className === "MessageMediaDocument") {
    const doc = media.document;
    const attrs: any[] = doc?.attributes || [];
    const videoAttr = attrs.find((a) => a.className === "DocumentAttributeVideo");
    const isVideo = !!videoAttr || String(doc?.mimeType || "").startsWith("video/");
    if (isVideo) {
      // Carry the real pixel dimensions through. A <video> with a poster but no
      // src (we defer the download until play) has NO intrinsic size, so
      // max-width/max-height alone leave it at the CSS default 300x150 — or
      // collapsed to a sliver with the controls floating over it. w/h give it a
      // correct box before a single byte of video is fetched.
      return { kind: "video", w: videoAttr?.w, h: videoAttr?.h };
    }
    const fileAttr = attrs.find((a) => a.className === "DocumentAttributeFilename");
    const name = fileAttr?.fileName || "file";
    // iPhone HEIC/HEIF (and any image sent as a file) arrives as a document, not
    // MessageMediaPhoto. The browser can't render HEIC, but Telegram attaches a
    // JPEG thumbnail — show that inline, like a video poster. Click downloads the
    // original. See docs/superpowers/specs (no-draft model: ids only, no content).
    const isImage = String(doc?.mimeType || "").startsWith("image/");
    if (isImage) return { kind: "image-doc", name };
    return { kind: "document", name };
  }
  return null;
}

/** Index of the largest entry in a document's `thumbs` list, for downloadMedia's
 *  `thumb` option. Telegram thumbs vary (PhotoSize has w/h/size; cached/stripped
 *  variants don't), so rank by pixel area, then byte size, and skip the tiny
 *  stripped placeholder when a real size exists. Defaults to 0 if unknown. */
function largestThumbIndex(media: any): number {
  const thumbs: any[] = media?.document?.thumbs || [];
  if (thumbs.length <= 1) return 0;
  const area = (t: any) =>
    (t?.w || 0) * (t?.h || 0) || t?.size || (t?.bytes?.length ?? 0);
  let best = 0;
  for (let i = 1; i < thumbs.length; i++) {
    if (area(thumbs[i]) > area(thumbs[best])) best = i;
  }
  return best;
}

// Last-fetched message window per chat, for stale-while-revalidate: re-opening
// a feed paints instantly from here, then a background fetch refreshes it.
// Session-only (a JS Map, never persisted) — consistent with snail storing no
// letter content; this is the same data already live in the DOM.
const historyCache = new Map<number, any[]>();

// Cold threads paint a small window first (the view opens at the newest letter
// anyway), then extend to the full window in the background.
const FIRST_WINDOW = 50;
const FULL_WINDOW = 500;

/** Re-render the feed with a fresher window, but only if this feed is still on
 *  screen (routeMain replaces #main per navigation, so a detached element means
 *  the user navigated away — never paint chat A's history into chat B's feed).
 *  Keeps the reader anchored: re-pins to the last letter if they were there,
 *  otherwise offsets by the height of any older messages prepended above. */
function refreshFeed(
  client: any, chatId: number, msgs: any[],
  authors: Record<string, string>, feedEl: HTMLElement | null
): void {
  if (!feedEl?.isConnected) return;
  const c = scrollContainer();
  const wasAtLast = isAtOrBelowLastLetter();
  const oldHeight = c.scrollHeight;
  renderMessagesToFeed(client, chatId, msgs, authors);
  if (wasAtLast) requestAnimationFrame(scrollToLastLetter);
  else c.scrollTop += c.scrollHeight - oldHeight;
}

async function renderGroupHistory(
  client: any, chatId: number, authors: Record<string, string>
): Promise<void> {
  const cached = historyCache.get(chatId);
  if (cached) {
    // Paint immediately from cache, then revalidate in the background.
    renderMessagesToFeed(client, chatId, cached, authors);
    const feedEl = document.getElementById("feed-messages");
    void (async () => {
      try {
        const peer = await resolveChat(client, chatId);
        const fresh = await client.getMessages(peer, { limit: FULL_WINDOW });
        historyCache.set(chatId, fresh);
        refreshFeed(client, chatId, fresh, authors, feedEl);
      } catch (e) {
        console.error("background history refresh failed", e);
      }
    })();
    return;
  }
  // Cold: fetch a small window for a fast first paint, then extend to the full
  // window in the background.
  const peer = await resolveChat(client, chatId);
  const msgs = await client.getMessages(peer, { limit: FIRST_WINDOW });
  historyCache.set(chatId, msgs);
  renderMessagesToFeed(client, chatId, msgs, authors);
  if (msgs.length < FIRST_WINDOW) return; // whole history already fetched
  const feedEl = document.getElementById("feed-messages");
  void (async () => {
    try {
      const full = await client.getMessages(peer, { limit: FULL_WINDOW });
      historyCache.set(chatId, full);
      refreshFeed(client, chatId, full, authors, feedEl);
    } catch (e) {
      console.error("history window extension failed", e);
    }
  })();
}

/** Render a fetched message window into the feed. Pure of network I/O except
 *  background media hydration. */
function renderMessagesToFeed(
  client: any, chatId: number, msgs: any[], authors: Record<string, string>
): void {
  const ordered = [...msgs].reverse();

  const haveMap = Object.keys(authors).length > 0;

  // Collect messages. If we have author data, skip unattributed messages
  // (separators, confirmations). Otherwise keep everything so the feed
  // isn't blank for groups that predate the attribution feature.
  const messages: FeedMessage[] = [];
  for (const m of ordered) {
    const authorName = authors[String(m.id)] || "";
    if (haveMap && !authorName) continue;

    const text = m.message || "";
    if (!text && !m.media) continue;   // keep media-only messages

    const cls = classifyMedia(m.media);
    const media: MediaInfo | undefined = cls
      ? {
          raw: m.media, kind: cls.kind, name: cls.name,
          domId: `media-${mediaSeq++}`, cacheKey: `${chatId}:${m.id}`,
          w: cls.w, h: cls.h,
        }
      : undefined;

    const date = m.date ? new Date(m.date * 1000) : new Date();
    messages.push({ text, author: authorName, date, media });
  }

  if (messages.length === 0) {
    setFeed(`<p class="feed-empty">${t("feed.no_letters")}</p>`);
    return;
  }

  let html: string;
  if (haveMap) {
    // Group consecutive messages by the same author.
    const groups: LetterGroup[] = [];
    for (const msg of messages) {
      const last = groups[groups.length - 1];
      if (last && last.author === msg.author) {
        last.messages.push({ text: msg.text, date: msg.date, media: msg.media });
      } else {
        groups.push({
          author: msg.author,
          messages: [{ text: msg.text, date: msg.date, media: msg.media }],
        });
      }
    }
    html = groups.map((g) => {
      const when = g.messages[0].date.toLocaleDateString("en-US", {
        month: "short", day: "numeric",
      });
      // snail's own notices: a quiet line between letters, not a letter block
      // with an avatar and an author colour.
      if (g.author === SNAIL_AUTHOR) {
        return g.messages.map((m) => `
          <div class="feed-notice">
            <span class="feed-notice-icon" aria-hidden="true">⚙</span>
            <span class="feed-notice-text">${escapeHtml(m.text)}</span>
            <span class="feed-notice-date">${when}</span>
          </div>`).join("");
      }
      const color = authorColor(g.author);
      const bg = authorBg(g.author);
      const init = authorInitial(g.author);
      const body = g.messages.map(renderBody).join("");
      return `
        <div class="letter-group" style="border-left-color:${color};background:${bg}">
          <div class="letter-group-header">
            <span class="author-avatar" style="background:${color}">${init}</span>
            <span class="author-name" style="color:${color}">${escapeHtml(g.author)}</span>
            <span class="letter-date">${when}</span>
          </div>
          <div class="letter-body">${body}</div>
        </div>`;
    }).join("");
  } else {
    // No author data — show each message individually with just a date.
    html = messages.map((m) => {
      const when = m.date.toLocaleDateString("en-US", {
        month: "short", day: "numeric",
      });
      return `
        <div class="letter-group">
          <div class="letter-group-header">
            <span class="letter-date">${when}</span>
          </div>
          <div class="letter-body">${renderBody(m)}</div>
        </div>`;
    }).join("");
  }

  setFeed(html);

  // Skeletons are in the DOM; download thumbnails in the background and swap.
  // When media finishes settling, keep the reader anchored to the last letter
  // if they haven't scrolled up to older mail — image loads above it would
  // otherwise nudge the first line off-screen. Checked at settle time against
  // the live scroll position so we never yank someone reading older letters.
  hydrateMedia(client, messages, () => {
    if (isAtOrBelowLastLetter()) scrollToLastLetter();
  });
}

/** Render one message's text + media skeleton. */
function renderBody(m: { text: string; media?: MediaInfo }): string {
  const para = m.text ? `<p class="letter-para">${escapeHtml(m.text)}</p>` : "";
  if (!m.media) return para;
  const { kind, name, domId } = m.media;
  if (kind === "document") {
    return (
      para +
      `<a class="letter-media doc-link" id="${domId}" role="button">📎 ${escapeHtml(name || "file")}</a>`
    );
  }
  // photo / video — placeholder swapped to <img>/<video> once thumb loads.
  return para + `<div class="media-placeholder loading" id="${domId}">${t("feed.media_loading")}</div>`;
}

/** Download thumbnails for photo/video messages and swap placeholders in.
 *  `onSettled` fires once all thumb swaps resolve, so callers can re-anchor the
 *  scroll after layout shifts from loaded media. */
function hydrateMedia(
  client: any, messages: FeedMessage[], onSettled?: () => void
): void {
  const all = messages.filter((m) => m.media);
  const docs = all.filter((m) => m.media!.kind === "document");
  const thumbs = all.filter((m) => m.media!.kind !== "document");

  // Documents: wire click-to-download, no eager fetch.
  for (const m of docs) {
    const el = document.getElementById(m.media!.domId);
    if (el) el.addEventListener("click", () => downloadFull(client, m.media!));
  }

  // Photos/videos: fetch a preview, then swap. GramJS `thumb` is an INDEX into
  // the size list (0 = smallest); `-1` is out of range and returns undefined,
  // which left the placeholder stuck. Photos: download the full (already
  // compressed) photo, no thumb. Videos: largest thumb for the poster frame.
  // Swap the placeholder for the loaded preview, wiring click/play handlers.
  const swap: SwapFn = (info, url) => {
    const el = document.getElementById(info.domId);
    if (!el) return;
    if (info.kind === "photo" || info.kind === "image-doc") {
      el.outerHTML =
        `<img src="${url}" alt="" class="letter-media" loading="lazy" id="${info.domId}">`;
      document
        .getElementById(info.domId)
        ?.addEventListener("click", () => downloadFull(client, info));
    } else {
      // width/height attributes (not CSS) give the element an intrinsic aspect
      // ratio while it has no src; .letter-media's max-width/max-height then
      // scale it down to fit. Without them the browser uses the 300x150 default
      // and the poster renders as a sliver with controls floating over it.
      const dims =
        info.w && info.h ? ` width="${info.w}" height="${info.h}"` : "";
      // Empty url = no thumbnail available; omit poster entirely rather than
      // emitting poster="" (which browsers treat as a broken image).
      const poster = url ? ` poster="${url}"` : "";
      el.outerHTML =
        `<video class="letter-media" controls preload="none"${dims}${poster} id="${info.domId}"></video>`;
      const vid = document.getElementById(info.domId) as HTMLVideoElement | null;
      vid?.addEventListener(
        "play",
        async () => {
          if (vid.src) return;
          // The full video is large and only needed on explicit play — not
          // cached (the poster is what makes the feed feel loaded).
          try {
            const full = await client.downloadMedia(info.raw, {});
            if (!full) return;
            // Blob type must be the document's real mime: an iPhone .MOV is
            // video/quicktime, and a `qt`-branded container is one Chrome and
            // Firefox refuse to demux even though the H.264/AAC streams inside
            // are standard. Mislabelling it video/mp4 doesn't help and hides
            // the reason.
            const mime = info.raw?.document?.mimeType || "video/mp4";
            vid.src = URL.createObjectURL(new Blob([full], { type: mime }));
            // If the browser can't play this container, say so and offer the
            // file — silently showing a dead black box is what made this look
            // like the video was missing entirely.
            vid.addEventListener(
              "error",
              () => {
                console.error("video playback unsupported", mime);
                toast(t("feed.video_unsupported"));
                void downloadFull(client, info);
              },
              { once: true }
            );
          } catch (e) {
            console.error("video download failed", e);
            toast(t("feed.download_failed"));
          }
        },
        { once: true }
      );
    }
  };

  void Promise.allSettled(
    thumbs.map((m) => hydrateThumb(client, m.media!, swap))
  ).then(() => onSettled?.());
}

type SwapFn = (info: MediaInfo, url: string) => void;

// In-flight downloads keyed by cacheKey. The feed hydrates twice per view —
// FIRST_WINDOW then the FULL_WINDOW extension, or the historyCache paint then
// its background revalidation — re-rendering the same messages with fresh
// domIds. mediaCache only catches a COMPLETED download, so without this the
// second round always re-fires downloadMedia for images the first round is
// still fetching. Two concurrent requests contend for the same shared
// per-DC exported sender (see download.ts) and were observed stalling out
// past the 30s timeout ("couldn't load", fixed by reloading to a fresh
// client/sender). Deduping means the second round just awaits the first.
const inFlight = new Map<string, Promise<string>>();

/** Fetch one item's preview and swap it in. Any failure — rejection, empty
 *  result, or deadline (GramJS can hang forever on a dead media-DC sender) —
 *  flips the placeholder to a tap-to-retry state instead of leaving
 *  "loading…" stuck. */
export async function hydrateThumb(
  client: any, info: MediaInfo, swap: SwapFn,
  timeoutMs: number = DOWNLOAD_TIMEOUT_MS
): Promise<void> {
  // Already downloaded this session → reuse the cached blob, no network.
  const cached = mediaCache.get(info.cacheKey);
  if (cached) { swap(info, cached); return; }
  const pending = inFlight.get(info.cacheKey);
  if (pending) {
    try { swap(info, await pending); } catch (e) { markThumbFailed(client, info, swap, e); }
    return;
  }
  const attempt = (async () => {
    // Reload survivor: a preview downloaded in a past session renders without
    // touching the network. Inside the attempt so dedup/inFlight still hold.
    const stored = await getPreview(info.cacheKey);
    if (stored) {
      const url = URL.createObjectURL(stored);
      mediaCache.set(info.cacheKey, url);
      return url;
    }
    // Photo → smallest size >= 800px; video → smallest thumb as poster;
    // image-doc (HEIC etc.) → largest embedded JPEG thumb, since the browser
    // can't decode the original and the thumb is all we can render inline.
    const previewSize = info.kind === "photo" ? photoPreviewSize(info.raw) : undefined;
    const opts =
      info.kind === "photo" ? (previewSize ? { thumb: previewSize } : {})
      : info.kind === "image-doc" ? { thumb: largestThumbIndex(info.raw) }
      : { thumb: 0 };
    const buf = await mediaDownloadLimit(() =>
      downloadWithTimeout(client, info.raw, opts, timeoutMs, (late) => {
        if (mediaCache.has(info.cacheKey)) return;
        const blob = new Blob([late], { type: "image/jpeg" });
        mediaCache.set(info.cacheKey, URL.createObjectURL(blob));
        putPreview(info.cacheKey, blob);
      })
    );
    if (!buf) throw new Error("empty download");
    const blob = new Blob([buf], { type: "image/jpeg" });
    const url = URL.createObjectURL(blob);
    mediaCache.set(info.cacheKey, url);
    putPreview(info.cacheKey, blob);
    return url;
  })();
  inFlight.set(info.cacheKey, attempt);
  // .finally() derives a NEW promise that inherits attempt's rejection; the
  // try/catch below only handles `attempt` itself, so without this .catch the
  // derived promise is an unhandled rejection on every failed thumbnail.
  attempt.finally(() => {
    // Only the owner clears its own entry — a retry that started a NEW
    // attempt for the same key must not have its (still in-flight) promise
    // evicted by this stale finally.
    if (inFlight.get(info.cacheKey) === attempt) inFlight.delete(info.cacheKey);
  }).catch(() => {});
  try {
    const url = await attempt;
    swap(info, url);
  } catch (e) {
    console.error("media thumb failed", e);
    // A video without a usable poster is still perfectly playable — Telegram
    // doesn't always attach a thumbnail (`thumbs` can be empty), and treating
    // that as a media failure hid the video entirely behind "couldn't load".
    // Render it posterless so it can still be played; only genuinely
    // un-renderable media falls through to the retry state.
    if (info.kind === "video") { swap(info, ""); return; }
    markThumbFailed(client, info, swap, e);
  }
}

function markThumbFailed(
  client: any, info: MediaInfo, swap: SwapFn, err?: unknown
): void {
  const el = document.getElementById(info.domId);
  if (!el) return;
  el.className = "media-placeholder failed";
  el.textContent = t("feed.media_failed");
  el.addEventListener(
    "click",
    () => {
      // A timeout usually means the media-DC sender is dead; retrying through
      // the same hung sender can never succeed. Reset so this retry builds a
      // fresh one. Other failures don't implicate the sender.
      if (err instanceof Error && err.message === DOWNLOAD_TIMEOUT_MESSAGE) {
        resetMediaSenders(client);
      }
      el.className = "media-placeholder loading";
      el.textContent = t("feed.media_loading");
      void hydrateThumb(client, info, swap);
    },
    { once: true }
  );
}

/** Download full media. Documents (incl. HEIC, which the browser can't render
 *  inline) save to disk under their filename; photos/videos open in a new tab. */
async function downloadFull(client: any, info: MediaInfo): Promise<void> {
  try {
    const buf = await client.downloadMedia(info.raw, {});
    if (!buf) return;
    // Untyped blobs open as text/octet-stream in a new tab (Chrome renders it
    // as raw text rather than an image/video). Photos are always JPEG-
    // compressed by Telegram; documents/image-docs keep their own mime type
    // when known, otherwise fall back to octet-stream for the download.
    const mime =
      info.kind === "photo" ? "image/jpeg"
      // Use the document's OWN mime type for video: iPhone .MOV files are
      // video/quicktime, and labelling them video/mp4 misleads the browser
      // about a container it may not be able to demux anyway.
      : info.raw?.document?.mimeType
        || (info.kind === "video" ? "video/mp4" : "application/octet-stream");
    const url = URL.createObjectURL(new Blob([buf], { type: mime }));
    if (info.kind === "image-doc" || info.kind === "document") {
      const a = document.createElement("a");
      a.href = url;
      a.download = info.name || "file";
      a.click();
    } else {
      window.open(url, "_blank");
    }
  } catch (e) {
    console.error("media download failed", e);
    toast(t("feed.download_failed"));
  }
}

function setFeed(html: string): void {
  const el = document.getElementById("feed-messages");
  if (el) el.innerHTML = html;
}

/** The element that actually scrolls. The feed isn't height-constrained, so
 *  #feed-messages never overflows — `main` is the overflow-y:auto container.
 *  Walk up from the feed to the nearest scrollable ancestor (falls back to
 *  documentElement). */
function scrollContainer(): HTMLElement {
  let el: HTMLElement | null = document.getElementById("feed-messages");
  while (el && el !== document.body) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement as HTMLElement || document.documentElement;
}

function lastLetterEl(): HTMLElement | null {
  const feedEl = document.getElementById("feed-messages");
  if (!feedEl) return null;
  const groups = feedEl.querySelectorAll<HTMLElement>(".letter-group");
  return groups[groups.length - 1] ?? null;
}

/** Scroll so the top of the last writer's letter group sits at the top of the
 *  viewport. Falls back to the bottom of the scroll container when there are no
 *  grouped letters. */
function scrollToLastLetter(): void {
  const last = lastLetterEl();
  if (!last) {
    const c = scrollContainer();
    c.scrollTop = c.scrollHeight;
    return;
  }
  // scrollIntoView walks to the correct scrollable ancestor on its own, so it
  // works regardless of which element overflows (here: `main`, not the feed).
  last.scrollIntoView({ block: "start" });
}

/** True when the viewport is already at or past the start of the last letter —
 *  i.e. the reader hasn't scrolled up into older mail. Used to decide whether a
 *  late media load may re-pin the view without disturbing the reader. */
function isAtOrBelowLastLetter(): boolean {
  const last = lastLetterEl();
  if (!last) return true;
  const c = scrollContainer();
  // The last letter's offset within the scroll container.
  const target = last.getBoundingClientRect().top - c.getBoundingClientRect().top + c.scrollTop;
  // Small slack so sub-pixel rounding / a hair of overscroll still counts.
  return c.scrollTop >= target - 4;
}
